import type { ListItem, List } from '../types/list';
import type { InventoryItem } from '../types/inventory';
import { useTaxonomyStore } from '../store/useTaxonomyStore';

// Common mappings for real-time categorisation
// Key: Keyword to search for in item name
// Value: [CategoryId, Emoji, OptionalAlternateCategoryIds]
const DICTIONARY: Record<string, [string, string, string[]?]> = {
  // Produce (Fruits & Vegetables - EN, AF, ES, DE, FR, IT, NL, JA)
  'apple': ['produce', '🍎'],
  'appel': ['produce', '🍎'], // AF / NL
  'manzana': ['produce', '🍎'], // ES
  'apfel': ['produce', '🍎'], // DE
  'pomme': ['produce', '🍎'], // FR
  'mela': ['produce', '🍎'], // IT
  'りんご': ['produce', '🍎'], // JA
  'リンゴ': ['produce', '🍎'], // JA

  'banana': ['produce', '🍌'],
  'piesang': ['produce', '🍌'], // AF
  'plátano': ['produce', '🍌'], // ES
  'platano': ['produce', '🍌'], // ES
  'banane': ['produce', '🍌'], // DE / FR
  'banaan': ['produce', '🍌'], // NL
  'バナナ': ['produce', '🍌'], // JA

  'orange': ['produce', '🍊'],
  'lemoen': ['produce', '🍊'], // AF
  'naranja': ['produce', '🍊'], // ES
  'arancia': ['produce', '🍊'], // IT
  'sinaasappel': ['produce', '🍊'], // NL
  'オレンジ': ['produce', '🍊'], // JA

  'lemon': ['produce', '🍋'],
  'suurlemoen': ['produce', '🍋'], // AF
  'limón': ['produce', '🍋'], // ES
  'limon': ['produce', '🍋'], // ES
  'zitrone': ['produce', '🍋'], // DE
  'citron': ['produce', '🍋'], // FR
  'limone': ['produce', '🍋'], // IT
  'レモン': ['produce', '🍋'], // JA

  'lime': ['produce', '🍋‍🟩'],
  'lima': ['produce', '🍋‍🟩'], // ES / IT

  'grape': ['produce', '🍇'],
  'druif': ['produce', '🍇'], // AF / NL
  'uva': ['produce', '🍇'], // ES / IT
  'traube': ['produce', '🍇'], // DE
  'raisin': ['produce', '🍇'], // FR
  'ぶどう': ['produce', '🍇'], // JA
  'ブドウ': ['produce', '🍇'], // JA

  'strawberry': ['produce', '🍓'],
  'aarbei': ['produce', '🍓'], // AF / NL
  'fresa': ['produce', '🍓'], // ES
  'erdbeere': ['produce', '🍓'], // DE
  'fraise': ['produce', '🍓'], // FR
  'fragola': ['produce', '🍓'], // IT
  'いちご': ['produce', '🍓'], // JA
  'イチゴ': ['produce', '🍓'], // JA

  'berry': ['produce', '🫐'],
  'bes': ['produce', '🫐'], // NL
  'besse': ['produce', '🫐'], // AF
  'beere': ['produce', '🫐'], // DE
  'baie': ['produce', '🫐'], // FR

  'watermelon': ['produce', '🍉'],
  'waatlemoen': ['produce', '🍉'], // AF
  'sandía': ['produce', '🍉'], // ES
  'sandia': ['produce', '🍉'], // ES
  'wassermelone': ['produce', '🍉'], // DE
  'pastèque': ['produce', '🍉'], // FR
  'anguria': ['produce', '🍉'], // IT
  'watermeloen': ['produce', '🍉'], // NL
  'スイカ': ['produce', '🍉'], // JA

  'blik tamaties': ['pantry', '🥫'], // AF canned tomatoes
  'bliktamaties': ['pantry', '🥫'], // AF canned tomatoes
  'canned tomatoes': ['pantry', '🥫'],
  'tinned tomatoes': ['pantry', '🥫'],
  'tomato': ['produce', '🍅'],
  'tamatie': ['produce', '🍅'], // AF
  'tamaties': ['produce', '🍅'], // AF
  'tomate': ['produce', '🍅'], // ES / DE / FR
  'pomodoro': ['produce', '🍅'], // IT
  'tomaat': ['produce', '🍅'], // NL
  'トマト': ['produce', '🍅'], // JA

  'avocado': ['produce', '🥑'],
  'avokado': ['produce', '🥑'], // AF
  'aguacate': ['produce', '🥑'], // ES
  'avocat': ['produce', '🥑'], // FR
  'アボカド': ['produce', '🥑'], // JA

  'potato': ['produce', '🥔'],
  'aartappel': ['produce', '🥔'], // AF
  'patata': ['produce', '🥔'], // ES / IT
  'papa': ['produce', '🥔'], // ES
  'kartoffel': ['produce', '🥔'], // DE
  'pomme de terre': ['produce', '🥔'], // FR
  'aardappel': ['produce', '🥔'], // NL
  'じゃがいも': ['produce', '🥔'], // JA
  'ジャガイモ': ['produce', '🥔'], // JA

  'carrot': ['produce', '🥕'],
  'wortel': ['produce', '🥕'], // AF / NL
  'zanahoria': ['produce', '🥕'], // ES
  'karotte': ['produce', '🥕'], // DE
  'möhre': ['produce', '🥕'], // DE
  'carotte': ['produce', '🥕'], // FR
  'carota': ['produce', '🥕'], // IT
  'にんじん': ['produce', '🥕'], // JA
  'ニンジン': ['produce', '🥕'], // JA

  'corn': ['produce', '🌽'],
  'mielie': ['produce', '🌽'], // AF
  'maíz': ['produce', '🌽'], // ES
  'maiz': ['produce', '🌽'], // ES
  'mais': ['produce', '🌽'], // DE / FR / IT / NL
  'とうもろこし': ['produce', '🌽'], // JA

  'cucumber': ['produce', '🥒'],
  'komkommer': ['produce', '🥒'], // AF / NL
  'pepino': ['produce', '🥒'], // ES
  'gurke': ['produce', '🥒'], // DE
  'concombre': ['produce', '🥒'], // FR
  'cetriolo': ['produce', '🥒'], // IT
  'きゅうり': ['produce', '🥒'], // JA
  'キュウリ': ['produce', '🥒'], // JA

  'lettuce': ['produce', '🥬'],
  'blaarslaai': ['produce', '🥬'], // AF
  'lechuga': ['produce', '🥬'], // ES
  'salat': ['produce', '🥬'], // DE
  'laitue': ['produce', '🥬'], // FR
  'insalata': ['produce', '🥬'], // IT
  'sla': ['produce', '🥬'], // NL
  'レタス': ['produce', '🥬'], // JA

  'garlic': ['produce', '🧄'],
  'knoffel': ['produce', '🧄'], // AF
  'ajo': ['produce', '🧄'], // ES
  'knoblauch': ['produce', '🧄'], // DE
  'ail': ['produce', '🧄'], // FR
  'aglio': ['produce', '🧄'], // IT
  'knoflook': ['produce', '🧄'], // NL
  'にんにく': ['produce', '🧄'], // JA
  'ニンニク': ['produce', '🧄'], // JA

  'onion': ['produce', '🧅'],
  'ui': ['produce', '🧅'], // AF / NL
  'cebolla': ['produce', '🧅'], // ES
  'zwiebel': ['produce', '🧅'], // DE
  'oignon': ['produce', '🧅'], // FR
  'cipolla': ['produce', '🧅'], // IT
  'たまねぎ': ['produce', '🧅'], // JA
  'タマネギ': ['produce', '🧅'], // JA

  'mushroom': ['produce', '🍄'],
  'sampjoen': ['produce', '🍄'], // AF
  'seta': ['produce', '🍄'], // ES
  'champiñón': ['produce', '🍄'], // ES
  'pilz': ['produce', '🍄'], // DE
  'champignon': ['produce', '🍄'], // FR
  'fungo': ['produce', '🍄'], // IT
  'paddenstoel': ['produce', '🍄'], // NL
  'きのこ': ['produce', '🍄'], // JA
  'キノコ': ['produce', '🍄'], // JA

  'spinach': ['produce', '🥬'],
  'spinasie': ['produce', '🥬'], // AF
  'espinacas': ['produce', '🥬'], // ES
  'spinat': ['produce', '🥬'], // DE
  'épinard': ['produce', '🥬'], // FR
  'spinaci': ['produce', '🥬'], // IT
  'spinazie': ['produce', '🥬'], // NL
  'ほうれん草': ['produce', '🥬'], // JA

  'pumpkin': ['produce', '🎃'],
  'pampoen': ['produce', '🎃'], // AF
  'calabaza': ['produce', '🎃'], // ES
  'kürbis': ['produce', '🎃'], // DE
  'kurbis': ['produce', '🎃'], // DE
  'citrouille': ['produce', '🎃'], // FR
  'zucca': ['produce', '🎃'], // IT
  'pompoen': ['produce', '🎃'], // NL
  'かぼちゃ': ['produce', '🎃'], // JA
  'カボチャ': ['produce', '🎃'], // JA

  // Dairy & Eggs (EN, AF, ES, DE, FR, IT, NL, JA)
  'milk': ['dairy', '🥛'],
  'melk': ['dairy', '🥛'], // AF / NL
  'leche': ['dairy', '🥛'], // ES
  'milch': ['dairy', '🥛'], // DE
  'lait': ['dairy', '🥛'], // FR
  'latte': ['dairy', '🥛'], // IT
  '牛乳': ['dairy', '🥛'], // JA
  'ミルク': ['dairy', '🥛'], // JA

  'cheese': ['dairy', '🧀'],
  'kaas': ['dairy', '🧀'], // AF / NL
  'queso': ['dairy', '🧀'], // ES
  'käse': ['dairy', '🧀'], // DE
  'kase': ['dairy', '🧀'], // DE
  'fromage': ['dairy', '🧀'], // FR
  'formaggio': ['dairy', '🧀'], // IT
  'チーズ': ['dairy', '🧀'], // JA

  'butter': ['dairy', '🧈'],
  'botter': ['dairy', '🧈'], // AF
  'mantequilla': ['dairy', '🧈'], // ES
  'butter (de)': ['dairy', '🧈'], // DE
  'beurre': ['dairy', '🧈'], // FR
  'burro': ['dairy', '🧈'], // IT
  'boter': ['dairy', '🧈'], // NL
  'バター': ['dairy', '🧈'], // JA

  'egg': ['dairy', '🥚'],
  'eier': ['dairy', '🥚'], // AF / DE
  'eiers': ['dairy', '🥚'], // AF
  'huevo': ['dairy', '🥚'], // ES
  'huevos': ['dairy', '🥚'], // ES
  'oeuf': ['dairy', '🥚'], // FR
  'oeufs': ['dairy', '🥚'], // FR
  'uovo': ['dairy', '🥚'], // IT
  'uova': ['dairy', '🥚'], // IT
  'eieren': ['dairy', '🥚'], // NL
  '卵': ['dairy', '🥚'], // JA
  'タマゴ': ['dairy', '🥚'], // JA

  'yogurt': ['dairy', '🥣'],
  'joghurt': ['dairy', '🥣'], // AF / DE
  'yogur': ['dairy', '🥣'], // ES
  'yaourt': ['dairy', '🥣'], // FR
  'ヨーグルト': ['dairy', '🥣'], // JA

  // Meat (EN, AF, ES, DE, FR, IT, NL, JA)
  'meat': ['meat', '🥩'],
  'vleis': ['meat', '🥩'], // AF
  'carne': ['meat', '🥩'], // ES / IT
  'fleisch': ['meat', '🥩'], // DE
  'viande': ['meat', '🥩'], // FR
  'vlees': ['meat', '🥩'], // NL
  '肉': ['meat', '🥩'], // JA

  'beef': ['meat', '🥩'],
  'beesvleis': ['meat', '🥩'], // AF
  'ternera': ['meat', '🥩'], // ES
  'rindfleisch': ['meat', '🥩'], // DE
  'boeuf': ['meat', '🥩'], // FR
  'bœuf': ['meat', '🥩'], // FR
  'manzo': ['meat', '🥩'], // IT
  'rundvlees': ['meat', '🥩'], // NL
  '牛肉': ['meat', '🥩'], // JA

  'chicken': ['meat', '🍗'],
  'hoender': ['meat', '🍗'], // AF
  'pollo': ['meat', '🍗'], // ES / IT
  'hähnchen': ['meat', '🍗'], // DE
  'hahnchen': ['meat', '🍗'], // DE
  'poulet': ['meat', '🍗'], // FR
  'kip': ['meat', '🍗'], // NL
  '鶏肉': ['meat', '🍗'], // JA
  'チキン': ['meat', '🍗'], // JA

  'pork': ['meat', '🍖'],
  'varkvleis': ['meat', '🍖'], // AF
  'cerdo': ['meat', '🍖'], // ES
  'schweinefleisch': ['meat', '🍖'], // DE
  'porc': ['meat', '🍖'], // FR
  'maiale': ['meat', '🍖'], // IT
  'varkensvlees': ['meat', '🍖'], // NL
  '豚肉': ['meat', '🍖'], // JA

  'bacon': ['meat', '🥓'],
  'spek': ['meat', '🥓'], // AF / DE / NL
  'tocino': ['meat', '🥓'], // ES
  'lardon': ['meat', '🥓'], // FR
  'pancetta': ['meat', '🥓'], // IT
  'ベーコン': ['meat', '🥓'], // JA

  'sausage': ['meat', '🌭'],
  'wors': ['meat', '🌭'], // AF
  'salchicha': ['meat', '🌭'], // ES
  'wurst': ['meat', '🌭'], // DE
  'saucisse': ['meat', '🌭'], // FR
  'salsiccia': ['meat', '🌭'], // IT
  'worst': ['meat', '🌭'], // NL
  'ソーセージ': ['meat', '🌭'], // JA

  // Seafood (EN, AF, ES, DE, FR, IT, NL, JA)
  'fish': ['seafood', '🐟'],
  'vis': ['seafood', '🐟'], // AF / NL
  'pescado': ['seafood', '🐟'], // ES
  'fisch': ['seafood', '🐟'], // DE
  'poisson': ['seafood', '🐟'], // FR
  'pesce': ['seafood', '🐟'], // IT
  '魚': ['seafood', '🐟'], // JA

  'salmon': ['seafood', '🍣'],
  'salmón': ['seafood', '🍣'], // ES
  'lachs': ['seafood', '🍣'], // DE
  'saumon': ['seafood', '🍣'], // FR
  'salmone': ['seafood', '🍣'], // IT
  'zalm': ['seafood', '🍣'], // NL
  'サーモン': ['seafood', '🍣'], // JA

  'tuna': ['seafood', '🥫'],
  'tuneer': ['seafood', '🥫'], // AF
  'atún': ['seafood', '🥫'], // ES
  'atun': ['seafood', '🥫'], // ES
  'thunfisch': ['seafood', '🥫'], // DE
  'thon': ['seafood', '🥫'], // FR
  'tonno': ['seafood', '🥫'], // IT
  'tonijn': ['seafood', '🥫'], // NL
  'マグロ': ['seafood', '🥫'], // JA

  // Bakery (EN, AF, ES, DE, FR, IT, NL, JA)
  'bread': ['bakery', '🍞'],
  'brood': ['bakery', '🍞'], // AF / NL
  'pan': ['bakery', '🍞'], // ES
  'brot': ['bakery', '🍞'], // DE
  'pain': ['bakery', '🍞'], // FR
  'pane': ['bakery', '🍞'], // IT
  'パン': ['bakery', '🍞'], // JA

  'cake': ['bakery', '🍰'],
  'koek': ['bakery', '🍰'], // AF / NL
  'pastel': ['bakery', '🍰'], // ES
  'tarta': ['bakery', '🍰'], // ES
  'kuchen': ['bakery', '🍰'], // DE
  'gâteau': ['bakery', '🍰'], // FR
  'gateau': ['bakery', '🍰'], // FR
  'torta': ['bakery', '🍰'], // IT
  'taart': ['bakery', '🍰'], // NL
  'ケーキ': ['bakery', '🍰'], // JA

  // Pantry (EN, AF, ES, DE, FR, IT, NL, JA)
  'rice': ['pantry', '🍚'],
  'rys': ['pantry', '🍚'], // AF
  'arroz': ['pantry', '🍚'], // ES
  'reis': ['pantry', '🍚'], // DE
  'riz': ['pantry', '🍚'], // FR
  'riso': ['pantry', '🍚'], // IT
  'rijst': ['pantry', '🍚'], // NL
  '米': ['pantry', '🍚'], // JA
  'ライス': ['pantry', '🍚'], // JA

  'flour': ['pantry', '🌾'],
  'meel': ['pantry', '🌾'], // AF / DE
  'harina': ['pantry', '🌾'], // ES
  'farine': ['pantry', '🌾'], // FR
  'farina': ['pantry', '🌾'], // IT
  'bloem': ['pantry', '🌾'], // NL
  '小麦粉': ['pantry', '🌾'], // JA

  'sugar': ['pantry', '🧂'],
  'suiker': ['pantry', '🧂'], // AF / NL
  'azúcar': ['pantry', '🧂'], // ES
  'azucar': ['pantry', '🧂'], // ES
  'zucker': ['pantry', '🧂'], // DE
  'sucre': ['pantry', '🧂'], // FR
  'zucchero': ['pantry', '🧂'], // IT
  '砂糖': ['pantry', '🧂'], // JA

  'salt': ['pantry', '🧂'],
  'sout': ['pantry', '🧂'], // AF
  'sal': ['pantry', '🧂'], // ES
  'salz': ['pantry', '🧂'], // DE
  'sel': ['pantry', '🧂'], // FR
  'sale': ['pantry', '🧂'], // IT
  'zout': ['pantry', '🧂'], // NL
  '塩': ['pantry', '🧂'], // JA

  'oil': ['pantry', '🫙'],
  'olie': ['pantry', '🫙'], // AF / NL
  'aceite': ['pantry', '🫙'], // ES
  'öl': ['pantry', '🫙'], // DE
  'huile': ['pantry', '🫙'], // FR
  'olio': ['pantry', '🫙'], // IT
  '油': ['pantry', '🫙'], // JA

  'coffee': ['pantry', '☕'],
  'koffie': ['pantry', '☕'], // AF / NL
  'café': ['pantry', '☕'], // ES / FR
  'cafe': ['pantry', '☕'], // ES / FR
  'kaffee': ['pantry', '☕'], // DE
  'caffè': ['pantry', '☕'], // IT
  'caffe': ['pantry', '☕'], // IT
  'コーヒー': ['pantry', '☕'], // JA

  'tea': ['pantry', '🍵'],
  'tee': ['pantry', '🍵'], // AF / DE
  'té': ['pantry', '🍵'], // ES
  'te': ['pantry', '🍵'], // ES / IT
  'thé': ['pantry', '🍵'], // FR
  'the': ['pantry', '🍵'], // FR
  'tè': ['pantry', '🍵'], // IT
  'thee': ['pantry', '🍵'], // NL
  'お茶': ['pantry', '🍵'], // JA
  '紅茶': ['pantry', '🍵'], // JA

  // Beverages (EN, AF, ES, DE, FR, IT, NL, JA)
  'water': ['beverages', '💧'],
  'agua': ['beverages', '💧'], // ES
  'wasser': ['beverages', '💧'], // DE
  'eau': ['beverages', '💧'], // FR
  'acqua': ['beverages', '💧'], // IT
  '水': ['beverages', '💧'], // JA

  'juice': ['beverages', '🧃'],
  'sap': ['beverages', '🧃'], // AF / NL
  'jugo': ['beverages', '🧃'], // ES
  'zumo': ['beverages', '🧃'], // ES
  'saft': ['beverages', '🧃'], // DE
  'jus': ['beverages', '🧃'], // FR
  'succo': ['beverages', '🧃'], // IT
  'ジュース': ['beverages', '🧃'], // JA

  'beer': ['beverages', '🍺'],
  'bier': ['beverages', '🍺'], // AF / DE / NL
  'cerveza': ['beverages', '🍺'], // ES
  'bière': ['beverages', '🍺'], // FR
  'biere': ['beverages', '🍺'], // FR
  'birra': ['beverages', '🍺'], // IT
  'ビール': ['beverages', '🍺'], // JA

  'wine': ['beverages', '🍷'],
  'wyn': ['beverages', '🍷'], // AF
  'vino': ['beverages', '🍷'], // ES / IT
  'wein': ['beverages', '🍷'], // DE
  'vin': ['beverages', '🍷'], // FR
  'wijn': ['beverages', '🍷'], // NL
  'ワイン': ['beverages', '🍷'], // JA

  // Household (EN, AF, ES, DE, FR, IT, NL, JA)
  'soap': ['household', '🧼'],
  'seep': ['household', '🧼'], // AF
  'jabón': ['household', '🧼'], // ES
  'jabon': ['household', '🧼'], // ES
  'seife': ['household', '🧼'], // DE
  'savon': ['household', '🧼'], // FR
  'sapone': ['household', '🧼'], // IT
  'zeep': ['household', '🧼'], // NL
  '石鹸': ['household', '🧼'], // JA
  'せっけん': ['household', '🧼'], // JA
  'margarine': ['dairy', '🧈'],
  'sour cream': ['dairy', '🥣'],
  'cottage cheese': ['dairy', '🧀'],
  'cream cheese': ['dairy', '🧀'],
  'ghee': ['dairy', '🧈'],
  'whey': ['dairy', '🥛'],

  // Meat
  'steak': ['meat', '🥩'],
  'lamb': ['meat', '🥩'],
  'turkey': ['meat', '🦃'],
  'mince': ['meat', '🥩'],
  'ground beef': ['meat', '🥩'], // US
  'ground pork': ['meat', '🥩'],
  'veal': ['meat', '🥩'],
  'ham': ['meat', '🍖'],
  'prosciutto': ['meat', '🥓'],
  'salami': ['meat', '🥓'],
  'chorizo': ['meat', '🥓'],
  'duck': ['meat', '🍗'],

  // Seafood
  'shrimp': ['seafood', '🍤'], // US
  'prawn': ['seafood', '🍤'], // UK/AUS
  'crab': ['seafood', '🦀'],
  'lobster': ['seafood', '🦞'],
  'squid': ['seafood', '🦑'],
  'calamari': ['seafood', '🦑'],
  'oyster': ['seafood', '🦪'],
  'scallop': ['seafood', '🦪'],
  'mussel': ['seafood', '🦪'],
  'clam': ['seafood', '🦪'],
  'cod': ['seafood', '🐟'],
  'tilapia': ['seafood', '🐟'],
  'trout': ['seafood', '🐟'],

  // Bakery
  'baguette': ['bakery', '🥖'],
  'croissant': ['bakery', '🥐'],
  'bagel': ['bakery', '🥯'],
  'muffin': ['bakery', '🧁'],
  'pie': ['bakery', '🥧'],
  'cookie': ['bakery', '🍪'],
  'biscuit': ['bakery', '🍪'], // UK/AUS (sweet) or US (savory)
  'bun': ['bakery', '🍔'],
  'wrap': ['bakery', '🌯'],
  'tortilla': ['bakery', '🌮'],
  'pita': ['bakery', '🥙'],
  'flatbread': ['bakery', '🫓'],
  'pastry': ['bakery', '🥐'],
  'doughnut': ['bakery', '🍩'],
  'donut': ['bakery', '🍩'],
  'brownie': ['bakery', '🍫'],
  'scone': ['bakery', '🧁'],
  'loaf': ['bakery', '🍞'],

  // Pantry
  'pasta': ['pantry', '🍝'],
  'spaghetti': ['pantry', '🍝'],
  'macaroni': ['pantry', '🍝'],
  'penne': ['pantry', '🍝'],
  'noodle': ['pantry', '🍜'],
  'pepper (spice)': ['pantry', '🧂'],
  'black pepper': ['pantry', '🧂'],
  'spice': ['pantry', '🌶️'],
  'olive oil': ['pantry', '🫙'],
  'vinegar': ['pantry', '🫙'],
  'sauce': ['pantry', '🥫'],
  'ketchup': ['pantry', '🍅'],
  'tomato sauce': ['pantry', '🍅'], // AUS/NZ
  'mustard': ['pantry', '🌭'],
  'mayo': ['pantry', '🫙'],
  'mayonnaise': ['pantry', '🫙'],
  'cereal': ['pantry', '🥣'],
  'oats': ['pantry', '🥣'],
  'granola': ['pantry', '🥣'],
  'muesli': ['pantry', '🥣'],
  'soup': ['pantry', '🥫'],
  'broth': ['pantry', '🥫'],
  'stock': ['pantry', '🥫'],
  'bean': ['pantry', '🫘'],
  'lentil': ['pantry', '🫘'],
  'chickpea': ['pantry', '🫘'],
  'nut': ['pantry', '🥜'],
  'peanut': ['pantry', '🥜'],
  'almond': ['pantry', '🥜'],
  'walnut': ['pantry', '🥜'],
  'cashew': ['pantry', '🥜'],
  'honey': ['pantry', '🍯'],
  'jam': ['pantry', '🍓'],
  'jelly': ['pantry', '🍇'], // US
  'peanut butter': ['pantry', '🥜'],
  'syrup': ['pantry', '🥞'],
  'maple syrup': ['pantry', '🥞'],
  'chocolate': ['pantry', '🍫'],
  'baking': ['pantry', '🧁'],
  'yeast': ['pantry', '🍞'],
  'baking powder': ['pantry', '🧁'],
  'baking soda': ['pantry', '🧁'],
  'vanilla': ['pantry', '🍦'],

  // Snacks
  'chip': ['snacks', '🥔'], // US
  'crisp': ['snacks', '🥔'], // UK
  'popcorn': ['snacks', '🍿'],
  'cracker': ['snacks', '🍘'],
  'pretzel': ['snacks', '🥨'],
  'candy': ['snacks', '🍬'], // US
  'sweet': ['snacks', '🍭'], // UK
  'lolly': ['snacks', '🍭'], // AUS
  'lollies': ['snacks', '🍭'], // AUS
  'marshmallow': ['snacks', '🍡'],
  'gum': ['snacks', '🍬'],
  'mint (candy)': ['snacks', '🍬'],

  // Frozen
  'ice cream': ['frozen', '🍦'],
  'pizza': ['frozen', '🍕'],
  'frozen': ['frozen', '🥶'],
  'popsicle': ['frozen', '🧊'], // US
  'ice block': ['frozen', '🧊'], // AUS
  'ice lolly': ['frozen', '🧊'], // UK
  'ice': ['frozen', '🧊'],
  'gelato': ['frozen', '🍦'],
  'sorbet': ['frozen', '🍧'],

  // Beverages
  'soda': ['beverages', '🥤'], // US
  'pop': ['beverages', '🥤'], // US/Canada
  'fizzy drink': ['beverages', '🥤'], // UK
  'soft drink': ['beverages', '🥤'], // AUS
  'coke': ['beverages', '🥤'],
  'liquor': ['beverages', '🥃'],
  'spirit': ['beverages', '🥃'],
  'vodka': ['beverages', '🥃'],
  'whiskey': ['beverages', '🥃'],
  'whisky': ['beverages', '🥃'],
  'rum': ['beverages', '🥃'],
  'gin': ['beverages', '🥃'],
  'drink': ['beverages', '🥤'],
  'kombucha': ['beverages', '🍹'],
  'energy drink': ['beverages', '⚡'],
  'sports drink': ['beverages', '🧃'],

  // Household
  'paper towel': ['household', '🧻'],
  'kitchen roll': ['household', '🧻'], // UK
  'toilet paper': ['household', '🧻'],
  'toilet roll': ['household', '🧻'], // UK/AUS
  'tissue': ['household', '🤧'],
  'napkin': ['household', '🍽️'],
  'serviette': ['household', '🍽️'], // UK/AUS
  'detergent': ['household', '🧺'],
  'washing powder': ['household', '🧺'], // UK/AUS
  'fabric softener': ['household', '🧺'],
  'cleaner': ['household', '🧽'],
  'bleach': ['household', '🧴'],
  'sponge': ['household', '🧽'],
  'trash bag': ['household', '🗑️'], // US
  'garbage bag': ['household', '🗑️'], // US/AUS
  'bin liner': ['household', '🗑️'], // UK/AUS
  'foil': ['household', '🥡'],
  'aluminum foil': ['household', '🥡'], // US
  'aluminium foil': ['household', '🥡'], // UK/AUS
  'alfoil': ['household', '🥡'], // AUS
  'plastic wrap': ['household', '🥡'], // US
  'cling film': ['household', '🥡'], // UK
  'cling wrap': ['household', '🥡'], // AUS
  'ziploc': ['household', '🥡'],
  'sandwich bag': ['household', '🥡'],
  'shampoo': ['household', '🧴'],
  'conditioner': ['household', '🧴'],
  'body wash': ['household', '🧼'],
  'shower gel': ['household', '🧼'],
  'toothpaste': ['household', '🪥'],
  'toothbrush': ['household', '🪥'],
  'deodorant': ['household', '🧴'],
  'lotion': ['household', '🧴'],
  'moisturizer': ['household', '🧴'],
  'sunscreen': ['household', '🧴'],
  'sunblock': ['household', '🧴'],
  'diaper': ['household', '👶'], // US
  'nappy': ['household', '👶'], // UK/AUS
  'wipe': ['household', '🧻'],
  'razor': ['household', '🪒'],
  'shaving cream': ['household', '🫧'],
  'tampon': ['household', '🩸'],
  'pad': ['household', '🩸'],
  'cotton swab': ['household', '🧴'], // US
  'cotton bud': ['household', '🧴'], // UK/AUS
  'q-tip': ['household', '🧴'],
  'band-aid': ['household', '🩹'],
  'plaster': ['household', '🩹'], // UK/AUS
  'battery': ['household', '🔋', ['tools']],
  'batteries': ['household', '🔋', ['tools']],
  'light bulb': ['household', '💡'],
  'matches': ['household', '🔥', ['tools']],
  'lighter': ['household', '🔥', ['tools']],
  'candle': ['household', '🕯️'],

  // Camping (Shelter)
  'tent': ['shelter', '⛺'],
  'tarp': ['shelter', '⛺'],
  'gazebo': ['shelter', '⛺'],
  'awning': ['shelter', '⛺'],
  'swag': ['shelter', '⛺'],
  'pegs': ['shelter', '⛺'],
  'mallet': ['shelter', '⛺'],

  // Camping (Sleeping)
  'sleeping bag': ['sleeping', '🛌'],
  'mattress': ['sleeping', '🛌'],
  'air bed': ['sleeping', '🛌'],
  'cot': ['sleeping', '🛌'],
  'pillow': ['sleeping', '🛌'],
  'blanket': ['sleeping', '🛌'],
  'mat': ['sleeping', '🛌'],

  // Camping (Cooking)
  'stove': ['cooking', '🍳'],
  'gas': ['cooking', '🍳'],
  'fuel': ['cooking', '🍳'],
  'cooler': ['cooking', '🧊'],
  'esky': ['cooking', '🧊'],
  'ice box': ['cooking', '🧊'],
  'pot': ['cooking', '🍲'],
  'cutlery': ['cooking', '🍴'],
  'plate': ['cooking', '🍽️'],
  'bowl': ['cooking', '🥣'],
  'mug': ['cooking', '☕'],
  'cup': ['cooking', '🥤'],

  // Camping (Tools)
  'axe': ['tools', '⚒️'],
  'hammer': ['tools', '⚒️'],
  'knife': ['tools', '🔪'],
  'multitool': ['tools', '⚒️'],
  'torch': ['tools', '🔦'],
  'headlamp': ['tools', '🔦'],
  'lantern': ['tools', '🔦'],
  'rope': ['tools', '🪢'],
  'cord': ['tools', '🪢'],

  // Camping (Clothing)
  'jacket': ['clothing-outdoors', '🧥'],
  'coat': ['clothing-outdoors', '🧥'],
  'boots': ['clothing-outdoors', '🥾'],
  'shoes': ['clothing-outdoors', '👟'],
  'socks': ['clothing-outdoors', '🧦'],
  'gloves': ['clothing-outdoors', '🧤'],
  'hat': ['clothing-outdoors', '🧢'],
  'beanie': ['clothing-outdoors', '🧢'],
  'pants': ['clothing-outdoors', '👖'],
  'shorts': ['clothing-outdoors', '👖'],
  'shirt': ['clothing-outdoors', '👕'],

  // Pet (maps to household/groceries generally, or pantry)
  'dog food': ['household', '🐶'],
  'cat food': ['household', '🐱'],
  'pet food': ['household', '🐾'],
  'cat litter': ['household', '🐈'],
  'dog treat': ['household', '🦴'],

  // Baby (maps to household generally)
  'baby formula': ['pantry', '🍼'],
  'baby food': ['pantry', '🍼'],
  'pacifier': ['household', '👶'],
  'dummy': ['household', '👶'], // AUS/UK

  // Health & Medicine
  'medicine': ['household', '💊', ['first-aid']],
  'pill': ['household', '💊', ['first-aid']],
  'painkiller': ['household', '💊', ['first-aid']],
  'ibuprofen': ['household', '💊', ['first-aid']],
  'paracetamol': ['household', '💊', ['first-aid']],
  'vitamin': ['household', '💊'],
  'supplement': ['household', '💊'],
  'cough drop': ['household', '🍬', ['first-aid']],
  'first aid': ['household', '🩹', ['first-aid']],
  'bandages': ['household', '🩹', ['first-aid']],
  'antiseptic': ['household', '🩹', ['first-aid']],
  'repellent': ['household', '🦟', ['first-aid']],
  'bug spray': ['household', '🦟', ['first-aid']],

  // Miscellaneous Kitchen
  'tupperware': ['household', '🥡'],
  'baking paper': ['household', '🥡'],
  'parchment paper': ['household', '🥡'],
  'muffin tin': ['household', '🧁'],
  'spatula': ['household', '🍳'],
  'dish soap': ['household', '🧼'],
  'dishwasher tablet': ['household', '🧼'],

  // Wine
  'cabernet': ['wine-red-cab', '🍷', ['wine-red']],
  'merlot': ['wine-red-merlot', '🍷', ['wine-red']],
  'pinot noir': ['wine-red-pinot', '🍷', ['wine-red']],
  'syrah': ['wine-red-syrah', '🍷', ['wine-red']],
  'shiraz': ['wine-red-syrah', '🍷', ['wine-red']],
  'malbec': ['wine-red-malbec', '🍷', ['wine-red']],
  'zinfandel': ['wine-red-blend', '🍷', ['wine-red']],
  'bordeaux': ['wine-red-blend', '🍷', ['wine-red']],
  'chardonnay': ['wine-white-chardonnay', '🥂', ['wine-white']],
  'sauvignon blanc': ['wine-white-sauvignon', '🥂', ['wine-white']],
  'pinot grigio': ['wine-white-pinotgrigio', '🥂', ['wine-white']],
  'riesling': ['wine-white-riesling', '🥂', ['wine-white']],
  'moscato': ['wine-white-blend', '🥂', ['wine-white']],
  'champagne': ['wine-sparkling-champagne', '🍾', ['wine-sparkling']],
  'prosecco': ['wine-sparkling-prosecco', '🍾', ['wine-sparkling']],
  'cava': ['wine-sparkling', '🍾'],
  'sparkling': ['wine-sparkling', '🍾'],
  'rosé': ['wine-rose', '🌸'],
  'rose wine': ['wine-rose', '🌸'],
  'port': ['wine-fortified-port', '🍷', ['wine-fortified']],
  'sherry': ['wine-fortified-sherry', '🥃', ['wine-fortified']],
  'dessert wine': ['wine-fortified', '🍯'],

  // Vinyl / Music
  'rock': ['rock', '🎸'],
  'metal': ['rock', '🎸'],
  'punk': ['rock', '🎸'],
  'indie': ['rock', '🎸'],
  'alternative': ['rock', '🎸'],
  'jazz': ['jazz', '🎷'],
  'blues': ['jazz', '🎷'],
  'electronic': ['electronic', '🎹'],
  'techno': ['electronic', '🎹'],
  'house': ['electronic', '🎹'],
  'dance': ['electronic', '🎹'],
  'edm': ['electronic', '🎹'],
  'hip hop': ['hip-hop', '🎤'],
  'rap': ['hip-hop', '🎤'],
  'r&b': ['hip-hop', '🎤'],

  // Books (Genres)
  'fiction': ['books-fiction', '📚'],
  'sci-fi': ['books-fiction-scifi', '👽', ['books-fiction']],
  'science fiction': ['books-fiction-scifi', '👽', ['books-fiction']],
  'fantasy': ['books-fiction-fantasy', '🐉', ['books-fiction']],
  'thriller': ['books-fiction-thriller', '🕵️‍♂️', ['books-fiction']],
  'mystery': ['books-fiction-thriller', '🕵️‍♂️', ['books-fiction']],
  'romance': ['books-fiction-romance', '❤️', ['books-fiction']],
  'historical fiction': ['books-fiction-historical', '🕰️', ['books-fiction']],
  
  'non-fiction': ['books-nonfiction', '📘'],
  'biography': ['books-nonfiction-biography', '👤', ['books-nonfiction']],
  'memoir': ['books-nonfiction-biography', '👤', ['books-nonfiction']],
  'self help': ['books-nonfiction-selfhelp', '🌱', ['books-nonfiction']],
  'history': ['books-nonfiction-history', '🏛️', ['books-nonfiction']],
  'politics': ['books-nonfiction-history', '🏛️', ['books-nonfiction']],
  'science book': ['books-nonfiction-science', '🔬', ['books-nonfiction']],
  'tech book': ['books-nonfiction-science', '⚙️', ['books-nonfiction']],
  'cooking': ['books-nonfiction-cooking', '🍳', ['books-nonfiction']],
  'cookbook': ['books-nonfiction-cooking', '🍳', ['books-nonfiction']],
  'recipe book': ['books-nonfiction-cooking', '🍳', ['books-nonfiction']],
  
  'children': ['books-childrens', '🧸'],
  'kids book': ['books-childrens', '🧸'],
};

function getCategoryAndEmoji(itemName: string): [string, string, string[]?] | null {
  const lowerName = itemName.toLowerCase();
  
  // Sort dictionary keys by length descending to match longest phrases first 
  // (e.g. "paper towel" before "paper")
  const keys = Object.keys(DICTIONARY).sort((a, b) => b.length - a.length);
  
  for (const key of keys) {
    if (lowerName.includes(key)) {
      return DICTIONARY[key];
    }
  }
  
  return null;
}

export function autoCategorizeListItem(item: Partial<ListItem>, list?: List): Partial<ListItem> {
  // If list is a To-Do, Simple list, or has taxonomy disabled, do NOT apply grocery categories
  if (!list || 
      list.list_type === 'todo' || 
      list.list_type === 'simple' || 
      list.templateId === 'todo' || 
      list.templateId === 'simple' || 
      list.template_id === 'todo' || 
      list.template_id === 'simple' || 
      list.settings?.taxonomy_set_id === 'none' ||
      list.name.toLowerCase().includes('to-do') ||
      list.name.toLowerCase().includes('todo') ||
      list.name.toLowerCase().includes('task')) {
    return item;
  }

  // Only auto-categorize if the user hasn't explicitly set a category, 
  // or if they used a default placeholder (like Groceries/General/Other/Uncategorized)
  if (item.category && 
      item.category.toLowerCase() !== 'groceries' && 
      item.category.toLowerCase() !== 'general' &&
      item.category.toLowerCase() !== 'uncategorized' &&
      item.category.toLowerCase() !== 'other') {
    return item;
  }

  const match = getCategoryAndEmoji(item.name || '');
  if (match) {
    const [categoryId, emoji, alts] = match;
    
    // Verify the category exists in the taxonomy store
    const { categories } = useTaxonomyStore.getState();
    const taxonomySetId = list?.settings?.taxonomy_set_id || 'groceries';
    
    const possibleIds = [categoryId, ...(alts || [])];
    
    // 1. Try to find an exact match in the current list's taxonomy
    let taxonomyCategory = categories.find(c => c.setId === taxonomySetId && possibleIds.includes(c.id));
    
    // 2. Fallback to any category
    if (!taxonomyCategory) {
      taxonomyCategory = categories.find(c => possibleIds.includes(c.id));
    }
    
    if (taxonomyCategory) {
      return {
        ...item,
        category: taxonomyCategory.id,
        emoji: item.emoji ? item.emoji : emoji,
      };
    }
  }

  return item;
}

const SEASONAL_KEYWORDS = [
  'sunscreen', 'sunblock', 'pumpkin spice', 'turkey brine', 'eggnog', 'christmas', 'halloween',
  'pool chlorine', 'insect repellent', 'bug spray', 'fireplace log', 'flu medicine', 'de-icer', 'snow melt'
];

const ONE_OFF_KEYWORDS = [
  'party dip', 'feta dip', 'dip', 'birthday balloon', 'cake topper', 'renovation', 'party hat', 'event', 'wood chips', 'charcoal', 'party favors'
];

export function detectItemLifecycle(name: string): 'seasonal' | 'one_off' | 'staple' {
  const lower = name.toLowerCase();
  if (SEASONAL_KEYWORDS.some(k => lower.includes(k))) return 'seasonal';
  if (ONE_OFF_KEYWORDS.some(k => lower.includes(k))) return 'one_off';
  return 'staple';
}

export function autoCategorizeInventoryItem(item: Partial<InventoryItem>): Partial<InventoryItem> {
  const name = item.name || '';
  const detectedLifecycle = item.lifecycle || detectItemLifecycle(name);

  if (item.category && 
      item.category.toLowerCase() !== 'groceries' && 
      item.category.toLowerCase() !== 'general' &&
      item.category.toLowerCase() !== 'uncategorized' &&
      item.category.toLowerCase() !== 'other') {
    return {
      ...item,
      lifecycle: detectedLifecycle
    };
  }

  const match = getCategoryAndEmoji(name);
  if (match) {
    const [categoryId, emoji, alts] = match;
    
    const { categories } = useTaxonomyStore.getState();
    const possibleIds = [categoryId, ...(alts || [])];
    const taxonomyCategory = categories.find(c => possibleIds.includes(c.id));
    
    if (taxonomyCategory) {
      return {
        ...item,
        category: taxonomyCategory.id,
        emoji: item.emoji ? item.emoji : emoji,
        lifecycle: detectedLifecycle
      };
    }
  }

  return {
    ...item,
    lifecycle: detectedLifecycle
  };
}
